import { useEffect, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import MainLayout from '../components/layout/MainLayout';
import { getCustomerSubscription, getCustomerUsage } from '../api/backendApi';
import { useAuth } from '../contexts/AuthContext';

const STATUS_LABELS = {
  not_configured: 'Not Configured',
  confirmed: 'Confirmed',
  scheduled: 'Scheduled',
  canceled: 'Canceled',
};

function customerFeature(feature) {
  return {
    name: feature?.name || 'Feature',
    description: feature?.description || '',
    billingUnit: feature?.billingUnit || '',
    retailPrice: feature?.retailPrice || '',
    allowance: feature?.allowance || '',
  };
}

export default function SubscriptionSummaryPage() {
  const { userData } = useAuth();
  const tenantId = userData?.tenantId || userData?.tenant_id || '';
  const [summary, setSummary] = useState(null);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!tenantId) {
      setSummary({ status: 'not_configured', features: [] });
      return undefined;
    }
    getCustomerSubscription(tenantId)
      .then((response) => {
        const data = response?.data || { status: 'not_configured', features: [] };
        setSummary(data);
        if (data.currency) {
          return getCustomerUsage(tenantId, data.currency).then((usageResponse) => setUsage(usageResponse?.data || null));
        }
        return null;
      })
      .catch(() => setError('The subscription could not be loaded.'));
    return undefined;
  }, [tenantId]);

  const features = (summary?.features || []).map(customerFeature);

  return (
    <MainLayout scrollMain>
      <Box sx={{ p: 3, display: 'grid', gap: 1 }}>
        <Typography variant="h5">Subscription</Typography>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {summary?.commercialBeta || usage?.commercialBeta ? (
          <Alert severity="info">{summary?.label || usage?.label || 'BETA ESTIMATE — NOT BILLABLE'}. No amount is payable.</Alert>
        ) : null}
        <Typography>Status: {STATUS_LABELS[summary?.status] || 'Not Configured'}</Typography>
        {summary?.currency ? <Typography>Currency: {summary.currency}</Typography> : null}
        {features.length ? features.map((feature) => (
          <Box key={`${feature.name}-${feature.retailPrice}`}>
            <Typography>{feature.name}</Typography>
            {feature.description ? <Typography variant="body2">{feature.description}</Typography> : null}
            <Typography>Retail: {feature.retailPrice}{feature.billingUnit ? ` per ${feature.billingUnit}` : ''}</Typography>
            {feature.allowance ? <Typography>Included allowance: {feature.allowance}</Typography> : null}
          </Box>
        )) : <Typography>No commercial subscription is configured.</Typography>}
        <Typography variant="h6" sx={{ mt: 2 }}>Usage</Typography>
        {(usage?.lines || []).map((line, index) => (
          <Typography key={`${line.featureName || 'usage'}-${index}`}>
            {line.featureName}: {line.quantity || '—'} {line.billingUnit || ''} · retail {line.retailAmount || '—'}
          </Typography>
        ))}
        {usage?.retailTotal ? <Typography>Retail total: {usage.retailTotal} {usage.currency}</Typography> : null}
      </Box>
    </MainLayout>
  );
}
